import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Stack,
  Tag,
  VStack,
} from '@chakra-ui/react';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { SelfAccreditationClaims } from '@common/models/InvestorAccountModel/types';
import React, { useState } from 'react';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { getInitialAccreditationState } from '../functions';

export const Accreditation: React.FC = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [editing, setEditing] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const { investorAccount, updateAccreditationInformation } = useInvestorAccountModel();
  const [claims, setClaims] = useState<SelfAccreditationClaims>(getInitialAccreditationState(investorAccount));
  const needsAttention =
    !claims.certification && !claims.employment && !claims.income && !claims.netWorth && !claims.none;

  const save = async () => {
    setIsSaving(true);
    const result = await updateAccreditationInformation(claims);
    setIsSaving(false);
    if (result.success) {
      setEditing(false);
      setShowError(false);
    } else {
      setShowError(true);
    }
  };

  const cancel = () => {
    setClaims(getInitialAccreditationState(investorAccount));
    setEditing(false);
  };

  const onChange = (claim: keyof SelfAccreditationClaims) => {
    setEditing(true);
    setShowError(false);
    if (claim === 'none') {
      setClaims({ certification: false, employment: false, income: false, netWorth: false, none: true });
    } else {
      const newClaims = { ...claims };
      newClaims[claim] = !claims[claim];
      if (!newClaims.certification && !newClaims.employment && !newClaims.income && !newClaims.netWorth) {
        newClaims.none = true;
      } else {
        newClaims.none = false;
      }
      setClaims(newClaims);
    }
  };

  return (
    <Grid templateColumns={['1fr 1fr']} gap={25} w="100%">
      <GridItem colSpan={2} display="flex" flexDirection="row" justifyContent="space-between">
        <Stack direction={['column', 'row']} spacing={[2, 5]}>
          <Heading id="exit-timing" fontSize={'21px'}>
            Accreditation
          </Heading>

          {needsAttention && (
            <Tag whiteSpace="nowrap" colorScheme={'yellow'}>
              Needs Attention
            </Tag>
          )}
        </Stack>

        {editing && (
          <HStack spacing={5}>
            <Button
              isLoading={isSaving}
              isDisabled={
                !claims.certification && !claims.employment && !claims.income && !claims.netWorth && !claims.none
              }
              onClick={() => save()}>
              Save
            </Button>
            <Button variant="link" onClick={() => cancel()}>
              Cancel
            </Button>
          </HStack>
        )}
      </GridItem>
      {showError && (
        <GridItem colSpan={2}>
          <Box display="flex" w="100%" justifyContent="center" px={'5px'}>
            <Alert status="error">
              <AlertIcon />
              There was an error processing your request, please try again
            </Alert>
          </Box>
        </GridItem>
      )}
      <GridItem colSpan={2} borderTopWidth={1} borderTopColor={'gray.50'} />

      <GridItem colSpan={[2]}>
        <FormControl>
          <FormLabel htmlFor="claims">Are you an accredited investor?</FormLabel>
          <VStack w={'100%'} letterSpacing="0.5px" fontWeight="600" gap="6px" alignItems="flex-start">
            <HStack
              cursor="pointer"
              onClick={() => {
                onChange('none');
              }}
              gap="20px"
              alignItems="flex-start">
              <Checkbox pointerEvents={'none'} isChecked={claims.none} mt="4px" />
              <TextBlock>No, I am not an accredited investor.</TextBlock>
            </HStack>
            <HStack
              cursor="pointer"
              onClick={() => {
                onChange('income');
              }}
              gap="20px"
              alignItems="flex-start">
              <Checkbox pointerEvents={'none'} isChecked={claims.income} mt="4px" />
              <TextBlock>
                Yes, I am an individual who has an income greater than $200,000 in each of the past two years or whose
                joint income with a spouse is greater than $300,000 for those years, and a reasonable expectation of the
                same income level in the current year.
              </TextBlock>
            </HStack>
            <HStack
              cursor="pointer"
              onClick={() => {
                onChange('netWorth');
              }}
              gap="20px"
              alignItems="flex-start">
              <Checkbox pointerEvents={'none'} isChecked={claims.netWorth} mt="4px" />
              <TextBlock>
                Yes, I am an individual who has a net worth, or joint net worth with the person’s spouse, that exceeds
                $1 million at the time of the investment (The net worth amount cannot include the value of the person's
                primary residence.)
              </TextBlock>
            </HStack>
            <HStack
              cursor="pointer"
              onClick={() => {
                onChange('certification');
              }}
              gap="20px"
              alignItems="flex-start">
              <Checkbox pointerEvents={'none'} isChecked={claims.certification} mt="4px" />
              <TextBlock>
                Yes, I am an individual who hold certain certificates, designations, or credentials, such as Series 7,
                Series 65, and Series 82 licenses.
              </TextBlock>
            </HStack>
            <HStack
              cursor="pointer"
              onClick={() => {
                onChange('employment');
              }}
              gap="20px"
              alignItems="flex-start">
              <Checkbox pointerEvents={'none'} isChecked={claims.employment} mt="4px" />
              <TextBlock>
                Yes, I am an individual who is a "knowledgeable employee" and affiliated with the offerings on the
                platform.
              </TextBlock>
            </HStack>
          </VStack>
        </FormControl>
      </GridItem>
    </Grid>
  );
};
